<script lang="ts">
  import ProductSelect from "@shared/ui/ProductSelect.svelte";
  import {
    createContractRevenueSchedule,
    createContractLineItem,
    deleteContractRevenueSchedule,
    deleteContractLineItem,
    patchContractRevenueSchedule,
    patchContractLineItem,
  } from "@shared/api/contracts";
  import {
    CONTRACT_LINE_ITEM_REVENUE_TYPE_LABELS,
    type ContractLineItem,
    type ContractLineItemPatchRequest,
    type ContractLineItemRevenueType,
    type ContractRevenueSchedule,
    type ContractRevenueSchedulePatchRequest,
  } from "@shared/types/contract";
  import type { Product } from "@domain/product/model";
  import { contractLineItemsStore } from "@entities/contract/store.svelte";
  import { productOptionsStore } from "@entities/product/store.svelte";
  import { nextSortOrder, sumScheduleRevenue } from "@domain/contract/line-items";

  interface Props {
    contractId?: string | null;
    canEdit?: boolean;
    autoload?: boolean;
    productOptions?: Product[] | null;
    items?: ContractLineItem[];
  }

  let {
    contractId = null,
    canEdit = true,
    autoload = true,
    productOptions = null,
    items = $bindable([]),
  }: Props = $props();

  let loading = $state(false);
  let error = $state<string | null>(null);
  let addName = $state("");
  let adding = $state(false);
  let savingId = $state<string | null>(null);
  let deletingId = $state<string | null>(null);
  let addingScheduleLineItemId = $state<string | null>(null);
  let savingScheduleId = $state<string | null>(null);
  let deletingScheduleId = $state<string | null>(null);
  let tempCounter = 0;

  const itemCountLabel = $derived(`${items.length}件`);
  // 商品選択肢: 呼び元から渡ってきた場合はそちら優先、なければ productOptionsStore を使う。
  const effectiveProductOptions = $derived(
    productOptions ?? productOptionsStore.data,
  );
  const isLocalMode = $derived(!contractId);

  // autoload: contractId が変わったら store 経由でロード。
  // store.data が更新されたら items に同期する (bind:items との互換性を保つ)。
  $effect(() => {
    if (!autoload) return;
    if (!contractId) {
      items = [];
      return;
    }
    if (contractId === contractLineItemsStore.loadedContractId) {
      // 既にロード済みなら store.data を items に同期するだけ。
      if (contractLineItemsStore.data !== null) {
        items = contractLineItemsStore.data;
      }
      return;
    }
    loading = true;
    void contractLineItemsStore.load(contractId).then(() => {
      loading = false;
      if (contractLineItemsStore.data !== null) {
        items = contractLineItemsStore.data;
      }
      if (contractLineItemsStore.error) {
        error = contractLineItemsStore.error;
        items = [];
      }
    });
  });

  // 商品選択肢が未ロードなら lazy load。
  $effect(() => {
    if (productOptions !== null) return;
    if (productOptionsStore.data.length > 0 || productOptionsStore.loading) return;
    void productOptionsStore.load();
    return () => {
      productOptionsStore.clear();
    };
  });

  function updateLocal(id: string, patch: Partial<ContractLineItem>) {
    items = items.map((item) =>
      item.id === id ? { ...item, ...patch } : item,
    );
  }

  function updateScheduleLocal(
    lineItemId: string,
    scheduleId: string,
    patch: Partial<ContractRevenueSchedule>,
  ) {
    items = items.map((item) => {
      if (item.id !== lineItemId) return item;
      const schedules = item.schedules.map((schedule) =>
        schedule.id === scheduleId ? { ...schedule, ...patch } : schedule,
      );
      return {
        ...item,
        schedules,
        schedule_revenue_total: sumScheduleRevenue(schedules),
      };
    });
  }

  function appendScheduleLocal(
    lineItemId: string,
    schedule: ContractRevenueSchedule,
  ) {
    items = items.map((item) => {
      if (item.id !== lineItemId) return item;
      const schedules = [...item.schedules, schedule];
      return {
        ...item,
        schedules,
        schedule_revenue_total: sumScheduleRevenue(schedules),
      };
    });
  }

  function removeScheduleLocal(lineItemId: string, scheduleId: string) {
    items = items.map((item) => {
      if (item.id !== lineItemId) return item;
      const schedules = item.schedules.filter(
        (schedule) => schedule.id !== scheduleId,
      );
      return {
        ...item,
        schedules,
        schedule_revenue_total: sumScheduleRevenue(schedules),
      };
    });
  }

  function blankToNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  function decimalToNull(value: string | null): string | null {
    if (value === null) return null;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  function intToNull(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) ? parsed : null;
  }

  function monthToDate(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length > 0 ? `${trimmed}-01` : null;
  }

  function dateToMonth(value: string | null): string {
    return value ? value.slice(0, 7) : "";
  }

  function newTempId(prefix: string): string {
    tempCounter += 1;
    return `${prefix}_${Date.now()}_${tempCounter}`;
  }

  function todayDate(): string {
    return new Date().toISOString().slice(0, 10);
  }

  function createLocalItem(name: string): ContractLineItem {
    const now = new Date().toISOString();
    return {
      id: newTempId("tmp_cli"),
      contract_id: contractId ?? "",
      firm_id: "",
      product_id: null,
      name,
      module_type: null,
      revenue_type: null,
      acceptance_status: null,
      schedule_start_on: null,
      schedule_end_on: null,
      recognition_month: null,
      proposed_amount_ex_tax: null,
      split_count: null,
      sort_order: nextSortOrder(items),
      schedule_revenue_total: "0.00",
      schedules: [],
      created_at: now,
      updated_at: now,
    };
  }

  function createLocalSchedule(lineItemId: string): ContractRevenueSchedule {
    const now = new Date().toISOString();
    return {
      id: newTempId("tmp_crs"),
      line_item_id: lineItemId,
      firm_id: "",
      schedule_date: todayDate(),
      revenue: null,
      billing_amount: null,
      created_at: now,
      updated_at: now,
    };
  }

  function buildPatch(item: ContractLineItem): ContractLineItemPatchRequest {
    return {
      product_id: item.product_id,
      name: item.name.trim(),
      module_type: item.module_type,
      revenue_type: item.revenue_type,
      acceptance_status: item.acceptance_status,
      schedule_start_on: item.schedule_start_on,
      schedule_end_on: item.schedule_end_on,
      recognition_month: item.recognition_month,
      proposed_amount_ex_tax: item.proposed_amount_ex_tax,
      split_count: item.split_count,
      sort_order: item.sort_order,
    };
  }

  function buildSchedulePatch(
    schedule: ContractRevenueSchedule,
  ): ContractRevenueSchedulePatchRequest {
    return {
      schedule_date: schedule.schedule_date,
      revenue: schedule.revenue,
      billing_amount: schedule.billing_amount,
    };
  }

  async function addItem() {
    if (adding || !canEdit) return;
    const name = addName.trim();
    if (!name) {
      error = "商品名を入力してください";
      return;
    }
    adding = true;
    error = null;
    try {
      if (isLocalMode) {
        items = [...items, createLocalItem(name)];
        addName = "";
        return;
      }
      if (!contractId) return;
      const created = await createContractLineItem(contractId, {
        name,
        sort_order: nextSortOrder(items),
      });
      items = [...items, { ...created, schedules: created.schedules ?? [] }];
      addName = "";
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      adding = false;
    }
  }

  async function saveItem(item: ContractLineItem) {
    if (!contractId || savingId || !canEdit) return;
    if (!item.name.trim()) {
      error = "商品名を入力してください";
      return;
    }
    savingId = item.id;
    error = null;
    try {
      const updated = await patchContractLineItem(
        contractId,
        item.id,
        buildPatch(item),
      );
      updateLocal(item.id, updated);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      savingId = null;
    }
  }

  async function removeItem(item: ContractLineItem) {
    if (deletingId || !canEdit) return;
    if (isLocalMode || !contractId) {
      items = items.filter((row) => row.id !== item.id);
      return;
    }
    deletingId = item.id;
    error = null;
    try {
      await deleteContractLineItem(contractId, item.id);
      items = items.filter((row) => row.id !== item.id);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      deletingId = null;
    }
  }

  async function addSchedule(item: ContractLineItem) {
    if (addingScheduleLineItemId || !canEdit) return;
    addingScheduleLineItemId = item.id;
    error = null;
    try {
      if (isLocalMode || !contractId) {
        appendScheduleLocal(item.id, createLocalSchedule(item.id));
        return;
      }
      const created = await createContractRevenueSchedule(contractId, item.id, {
        schedule_date: todayDate(),
        revenue: null,
        billing_amount: null,
      });
      appendScheduleLocal(item.id, created);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      addingScheduleLineItemId = null;
    }
  }

  async function saveSchedule(
    item: ContractLineItem,
    schedule: ContractRevenueSchedule,
  ) {
    if (!contractId || savingScheduleId || !canEdit) return;
    savingScheduleId = schedule.id;
    error = null;
    try {
      const updated = await patchContractRevenueSchedule(
        contractId,
        item.id,
        schedule.id,
        buildSchedulePatch(schedule),
      );
      updateScheduleLocal(item.id, schedule.id, updated);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      savingScheduleId = null;
    }
  }

  async function removeSchedule(
    item: ContractLineItem,
    schedule: ContractRevenueSchedule,
  ) {
    if (deletingScheduleId || !canEdit) return;
    if (isLocalMode || !contractId) {
      removeScheduleLocal(item.id, schedule.id);
      return;
    }
    deletingScheduleId = schedule.id;
    error = null;
    try {
      await deleteContractRevenueSchedule(contractId, item.id, schedule.id);
      removeScheduleLocal(item.id, schedule.id);
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      deletingScheduleId = null;
    }
  }
</script>

<section class="line-items">
  <header class="header">
    <div>
      <h3>明細</h3>
      <p>{itemCountLabel}</p>
    </div>
    {#if canEdit}
      <div class="add-row">
        <input
          bind:value={addName}
          placeholder="明細名"
          disabled={adding}
        />
        <button
          type="button"
          class="secondary"
          onclick={addItem}
          disabled={adding}
        >
          {adding ? "追加中…" : "追加"}
        </button>
      </div>
    {/if}
  </header>

  {#if loading}
    <p class="hint">読み込み中…</p>
  {:else if items.length === 0}
    <p class="hint">商談商品は未登録です。</p>
  {:else}
    <div class="rows">
      {#each items as item (item.id)}
        <article class="line-item">
          <div class="grid">
            <label class="wide">
              <span>明細名</span>
              <input
                value={item.name}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    name: (ev.currentTarget as HTMLInputElement).value,
                  })}
              />
            </label>
            <label>
              <span>商品</span>
              <ProductSelect
                value={item.product_id ?? ""}
                disabled={!canEdit || savingId === item.id}
                placeholder="未設定"
                products={effectiveProductOptions}
                onChange={(value) =>
                  updateLocal(item.id, { product_id: value || null })}
              />
            </label>
            <label>
              <span>モジュール</span>
              <input
                value={item.module_type ?? ""}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    module_type: blankToNull(
                      (ev.currentTarget as HTMLInputElement).value,
                    ),
                  })}
              />
            </label>
            <label>
              <span>収益タイプ</span>
              <select
                value={item.revenue_type ?? ""}
                disabled={!canEdit || savingId === item.id}
                onchange={(ev) =>
                  updateLocal(item.id, {
                    revenue_type:
                      ((ev.currentTarget as HTMLSelectElement)
                        .value as ContractLineItemRevenueType) || null,
                  })}
              >
                <option value="">未設定</option>
                {#each Object.entries(CONTRACT_LINE_ITEM_REVENUE_TYPE_LABELS) as [value, label] (value)}
                  <option {value}>{label}</option>
                {/each}
              </select>
            </label>
            <label>
              <span>受注状況</span>
              <input
                value={item.acceptance_status ?? ""}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    acceptance_status: blankToNull(
                      (ev.currentTarget as HTMLInputElement).value,
                    ),
                  })}
              />
            </label>
            <label>
              <span>開始日</span>
              <input
                type="date"
                value={item.schedule_start_on ?? ""}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    schedule_start_on:
                      (ev.currentTarget as HTMLInputElement).value || null,
                  })}
              />
            </label>
            <label>
              <span>終了日</span>
              <input
                type="date"
                value={item.schedule_end_on ?? ""}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    schedule_end_on:
                      (ev.currentTarget as HTMLInputElement).value || null,
                  })}
              />
            </label>
            <label>
              <span>計上月</span>
              <input
                type="month"
                value={dateToMonth(item.recognition_month)}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    recognition_month: monthToDate(
                      (ev.currentTarget as HTMLInputElement).value,
                    ),
                  })}
              />
            </label>
            <label>
              <span>提案金額(税抜)</span>
              <input
                inputmode="decimal"
                value={item.proposed_amount_ex_tax ?? ""}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    proposed_amount_ex_tax: decimalToNull(
                      (ev.currentTarget as HTMLInputElement).value,
                    ),
                  })}
              />
            </label>
            <label>
              <span>分割回数</span>
              <input
                inputmode="numeric"
                value={item.split_count ?? ""}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    split_count: intToNull(
                      (ev.currentTarget as HTMLInputElement).value,
                    ),
                  })}
              />
            </label>
            <label>
              <span>並び順</span>
              <input
                inputmode="numeric"
                value={item.sort_order}
                disabled={!canEdit || savingId === item.id}
                oninput={(ev) =>
                  updateLocal(item.id, {
                    sort_order:
                      intToNull((ev.currentTarget as HTMLInputElement).value) ??
                      0,
                  })}
              />
            </label>
            <label>
              <span>売上予定合計</span>
              <input value={item.schedule_revenue_total ?? "—"} readonly />
            </label>
          </div>
          <div class="schedules">
            <div class="schedule-header">
              <strong>収益スケジュール</strong>
              {#if canEdit}
                <button
                  type="button"
                  class="secondary compact"
                  onclick={() => addSchedule(item)}
                  disabled={addingScheduleLineItemId === item.id}
                >
                  {addingScheduleLineItemId === item.id ? "追加中…" : "行を追加"}
                </button>
              {/if}
            </div>
            {#if item.schedules.length === 0}
              <p class="hint">スケジュールは未登録です。</p>
            {:else}
              <div class="schedule-rows">
                {#each item.schedules as schedule (schedule.id)}
                  <div class="schedule-row">
                    <label>
                      <span>スケジュール日</span>
                      <input
                        type="date"
                        value={schedule.schedule_date}
                        disabled={!canEdit || savingScheduleId === schedule.id}
                        oninput={(ev) =>
                          updateScheduleLocal(item.id, schedule.id, {
                            schedule_date:
                              (ev.currentTarget as HTMLInputElement).value ||
                              todayDate(),
                          })}
                      />
                    </label>
                    <label>
                      <span>収益</span>
                      <input
                        inputmode="decimal"
                        value={schedule.revenue ?? ""}
                        disabled={!canEdit || savingScheduleId === schedule.id}
                        oninput={(ev) =>
                          updateScheduleLocal(item.id, schedule.id, {
                            revenue: decimalToNull(
                              (ev.currentTarget as HTMLInputElement).value,
                            ),
                          })}
                      />
                    </label>
                    <label>
                      <span>請求金額</span>
                      <input
                        inputmode="decimal"
                        value={schedule.billing_amount ?? ""}
                        disabled={!canEdit || savingScheduleId === schedule.id}
                        oninput={(ev) =>
                          updateScheduleLocal(item.id, schedule.id, {
                            billing_amount: decimalToNull(
                              (ev.currentTarget as HTMLInputElement).value,
                            ),
                          })}
                      />
                    </label>
                    {#if canEdit}
                      <div class="schedule-actions">
                        {#if !isLocalMode}
                          <button
                            type="button"
                            class="secondary compact"
                            onclick={() => saveSchedule(item, schedule)}
                            disabled={
                              savingScheduleId === schedule.id ||
                              deletingScheduleId === schedule.id
                            }
                          >
                            {savingScheduleId === schedule.id
                              ? "保存中…"
                              : "保存"}
                          </button>
                        {/if}
                        <button
                          type="button"
                          class="danger compact"
                          onclick={() => removeSchedule(item, schedule)}
                          disabled={
                            savingScheduleId === schedule.id ||
                            deletingScheduleId === schedule.id
                          }
                        >
                          {deletingScheduleId === schedule.id
                            ? "削除中…"
                            : "削除"}
                        </button>
                      </div>
                    {/if}
                  </div>
                {/each}
              </div>
            {/if}
          </div>
          {#if canEdit}
            <div class="row-actions">
              {#if !isLocalMode}
                <button
                  type="button"
                  class="secondary"
                  onclick={() => saveItem(item)}
                  disabled={savingId === item.id || deletingId === item.id}
                >
                  {savingId === item.id ? "保存中…" : "保存"}
                </button>
              {/if}
              <button
                type="button"
                class="danger"
                onclick={() => removeItem(item)}
                disabled={savingId === item.id || deletingId === item.id}
              >
                {deletingId === item.id ? "削除中…" : "削除"}
              </button>
            </div>
          {/if}
        </article>
      {/each}
    </div>
  {/if}

  {#if error}
    <p class="error">{error}</p>
  {/if}
</section>

<style>
  .line-items {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  }

  h3 {
    margin: 0;
    font-size: 0.95rem;
  }

  .header p {
    margin: 2px 0 0;
    color: #6b7280;
    font-size: 0.75rem;
  }

  .add-row {
    display: flex;
    gap: 8px;
  }

  .add-row input {
    min-width: 220px;
  }

  .rows {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .line-item {
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 12px;
    background: #fff;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  label.wide {
    grid-column: span 2;
  }

  span {
    color: #4b5563;
    font-size: 0.75rem;
    font-weight: 600;
  }

  input,
  select,
  :global(.line-item select) {
    width: 100%;
    min-width: 0;
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
    box-sizing: border-box;
  }

  input[readonly] {
    color: #6b7280;
    background: #f9fafb;
  }

  .row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 10px;
  }

  .schedules {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .schedule-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .schedule-header strong {
    color: #374151;
    font-size: 0.8rem;
  }

  .schedule-rows {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .schedule-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
    gap: 8px;
    align-items: end;
  }

  .schedule-actions {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  button {
    padding: 8px 14px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    font: inherit;
  }

  button.secondary {
    border-color: #2563eb;
    color: #2563eb;
  }

  button.danger {
    border-color: #fecaca;
    color: #b91c1c;
  }

  button.compact {
    padding: 6px 10px;
    font-size: 0.82rem;
    white-space: nowrap;
  }

  button:disabled,
  input:disabled,
  select:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .hint {
    margin: 0;
    color: #6b7280;
  }

  .error {
    margin: 0;
    color: #b91c1c;
  }

  @media (max-width: 900px) {
    .header,
    .add-row {
      flex-direction: column;
      align-items: stretch;
    }

    .add-row input {
      min-width: 0;
    }

    .grid {
      grid-template-columns: 1fr;
    }

    .schedule-row {
      grid-template-columns: 1fr;
    }

    label.wide {
      grid-column: span 1;
    }
  }
</style>
